import type { ClaimRow, SiteDetail } from "@plenum/shared/api";
import { useState } from "react";
import { ApiError, useReviewAction } from "../../lib/api";
import { fmtDate } from "../../lib/format";
import { Pill } from "../Badge";
import { hostOf } from "../Cite";
import { useToast } from "../Toast";

const STATUSES = ["all", "proposed", "accepted", "rejected", "superseded"] as const;

/** A claim value in one line: contacts as name, title and channels; objects as their first fields. */
function valueSummary(value: unknown): string {
  if (value === null || value === undefined) return "unknown";
  if (typeof value !== "object") return String(value);
  const v = value as Record<string, unknown>;
  if (typeof v.full_name === "string")
    return [v.full_name, v.title, v.email, v.phone].filter(Boolean).join(" · ");
  if ("value" in v && typeof v.value !== "object") return String(v.value);
  return Object.entries(v)
    .filter(([k, x]) => x !== null && typeof x !== "object" && k !== "confidence")
    .slice(0, 4)
    .map(([k, x]) => `${k.replace(/_/g, " ")} ${String(x)}`)
    .join(" · ");
}

/** `agent:exa-agent-ultra:quote-verified` and `agent:claude-sonnet-5:1.0.0` in words. */
function provenance(extractedBy: string): string {
  const [kind, who, version] = extractedBy.split(":");
  if (kind === "user") return `reviewer ${who ?? ""}`.trim();
  if (kind !== "agent" || !who) return extractedBy;
  const lead: Record<string, string> = {
    "exa-agent": "Exa Agent lead",
    "exa-agent-ultra": "Exa Agent Ultra lead",
    "firecrawl-agent": "Firecrawl Agent lead",
  };
  if (lead[who]) return `${lead[who]}${version === "quote-verified" ? ", quote verified" : ""}`;
  return version ? `${who} · prompt ${version}` : who;
}

/** Claims ledger (PRD §12.3 tab 5) with inline accept and reject for proposed claims (editor role). */
export function EvidenceTab({ detail }: { detail: SiteDetail }) {
  const [status, setStatus] = useState<(typeof STATUSES)[number]>("all");
  const act = useReviewAction();
  const toast = useToast();
  const decide = async (claimId: number, action: "accept" | "reject") => {
    try {
      const r = await act.mutateAsync({ claimId, action: { action } });
      toast(`Claim ${r.status}${r.applied ? ", applied" : ""}`);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Action failed", "danger");
    }
  };
  const unitLabel = new Map(detail.units.map((u) => [u.id, u.eia_generator_id ?? String(u.id)]));
  const rows = detail.claims.filter((c) => status === "all" || c.status === status);
  return (
    <div>
      <div className="mb-2 flex items-center gap-2 text-[12px]">
        <span style={{ color: "var(--muted)" }}>
          <span className="num">{detail.claims_summary.accepted}</span> accepted ·{" "}
          <span className="num">{detail.claims_summary.proposed}</span> proposed ·{" "}
          <span className="num">{detail.claims_summary.rejected}</span> rejected
        </span>
        <select
          className="input ml-auto"
          value={status}
          onChange={(e) => setStatus(e.target.value as typeof status)}
          aria-label="Filter claims by status"
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
      {rows.length === 0 ? (
        <p style={{ color: "var(--muted)" }}>
          No claims yet. Enrichment agents write one row per extracted value with a source URL and a
          verbatim quote; reviewer edits land here too.
        </p>
      ) : (
        <ul
          className="rounded border"
          style={{ borderColor: "var(--line)", background: "var(--panel)" }}
        >
          {rows.map((c, i) => (
            <ClaimItem
              key={c.id}
              c={c}
              n={i + 1}
              unit={
                c.entity_type === "unit"
                  ? (unitLabel.get(c.entity_id) ?? String(c.entity_id))
                  : null
              }
              onDecide={decide}
              busy={act.isPending}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function ClaimItem({
  c,
  n,
  unit,
  onDecide,
  busy,
}: {
  c: ClaimRow;
  n: number;
  unit: string | null;
  onDecide: (id: number, action: "accept" | "reject") => void;
  busy: boolean;
}) {
  const [raw, setRaw] = useState(false);
  return (
    <li className="contact-row">
      <div className="min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="eyebrow">
            {unit ? `unit ${unit} · ` : ""}
            {c.field.replace(/_/g, " ")}
          </span>
          <span className="font-medium">{valueSummary(c.value)}</span>
          <Pill
            tone={c.status === "accepted" ? "ok" : c.status === "rejected" ? "danger" : "muted"}
          >
            {c.status}
          </Pill>
          <span className="num text-[11px]" style={{ color: "var(--muted-2)" }}>
            {Math.round(c.confidence * 100)} %
          </span>
        </div>
        {c.quote ? <blockquote className="cite-quote">“{c.quote}”</blockquote> : null}
        <div className="cite-ref-line mt-0.5">
          <span className="cite-mark" aria-hidden="true">
            {n}
          </span>
          <a
            className="cite-ref-label"
            href={c.source.url}
            target="_blank"
            rel="noreferrer"
            title={c.source.url}
          >
            {c.source.title ?? hostOf(c.source.url)}
          </a>
          <span className="cite-sep">·</span>
          <span className="cite-host">{c.source.source_type}</span>
          <span className="cite-sep">·</span>
          <span className="cite-host">{provenance(c.extracted_by)}</span>
          <span className="cite-sep">·</span>
          <span className="cite-host">{fmtDate(c.created_at)}</span>
          {c.resolved_at ? (
            <>
              <span className="cite-sep">·</span>
              <span className="cite-host">resolved {fmtDate(c.resolved_at)}</span>
            </>
          ) : null}
          <button type="button" className="cite-toggle" onClick={() => setRaw((r) => !r)}>
            {raw ? "hide value" : "value"}
          </button>
        </div>
        {raw ? (
          <pre
            className="num mt-1 max-h-48 overflow-auto whitespace-pre-wrap text-[11px]"
            style={{ color: "var(--muted)" }}
          >
            {JSON.stringify(c.value, null, 2)}
          </pre>
        ) : null}
      </div>
      {c.status === "proposed" ? (
        <div className="contact-actions">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={busy}
            onClick={() => onDecide(c.id, "accept")}
          >
            Accept
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ color: "var(--danger)" }}
            disabled={busy}
            onClick={() => onDecide(c.id, "reject")}
          >
            Reject
          </button>
        </div>
      ) : (
        <span />
      )}
    </li>
  );
}
